import { useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import { css } from "@emotion/react";
import { CurrencyDropdown } from "@zapier/checkout";
import { defaultCurrency } from "@zapier/currency";
import {
  ButtonProps,
  SkeletonBlock,
  SkeletonLoader,
} from "@zapier/design-system";
import { useTrackingContext } from "@zapier/toolbox-tracking";
import { useDisplayCurrency, usePricingPageSize } from "@/hooks";
import { usePlans } from "../shared/usePlans";
import { usePricingPageContext } from "@/pages/pricing";
import {
  Currency,
  currencyTypeName,
  FeatureListing,
  Size,
  TooltipPosition,
} from "@/types";
import { hiddenOnMobile, visibleOnlyOnMobile } from "@/utils";
import { PlansConfigBar } from "../../PricingPage/PlansConfigBar";
import { PlanCard } from "./PlanCard";
import { IntervalToggle } from "../../PricingPage/IntervalToggle";
import { getAnnualDiscountPercentage } from "@/utils/getAnnualDiscountPercentage";
import { IntervalDropdown } from "../../PricingPage/IntervalDropdown";
import { PLAN_CHANGE_EVENT, geoCurrencyLoadedEventData } from "@/constants";
import { CurrencyChargeWarningBar } from "@/components/PricingPage/CurrencyChargeWarningBar";

type PlanCardCollectionProps = {
  plans?: {
    type: string;
    description: string;
    primaryCTA?: { text?: string; color?: ButtonProps["color"] };
    hidePrice?: boolean;
    hideTasks?: boolean;
    secondaryCTA?: { text?: string; href?: string };
    features?: FeatureListing[];
    priceDetails?: string;
  }[];
};

const Styles = {
  root: css({
    maxWidth: "1240px",
    padding: "0 20px",
    margin: "auto",
  }),

  cards: css({
    maxWidth: "1240px",
    display: "grid",
    gridTemplateColumns: "1fr",
    position: "relative",
    overflow: "hidden",
    gap: "45px",

    "@media (min-width: 715px)": {
      gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
      gap: "15px",
    },

    "@media (min-width: 896px)": {
      gridTemplateColumns: "repeat(auto-fit, minmax(256px, 1fr))",
    },

    "@media (min-width: 1024px)": {
      gridTemplateColumns: "repeat(auto-fit, minmax(288px, 1fr))",
    },

    "@media (min-width: 1235px)": {
      gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
      gap: "20px",
    },
  }),

  currencyDropdown: css({
    width: "160px",
  }),

  intervalDropdown: css({
    width: "250px",
  }),
};

export default function PlanCardCollection({
  plans = [],
}: PlanCardCollectionProps) {
  const { countryCode, geolocatedCurrency } = usePricingPageContext();
  const { planStacks, isLoading: isLoadingPlanStacks } = usePlans();
  const { trackEvent } = useTrackingContext();
  const {
    currencyType,
    displayCurrency,
    isLoading: isLoadingCurrency,
  } = useDisplayCurrency({ countryCode });
  const size: Size = usePricingPageSize();

  const [isAnnual, setIsAnnual] = useState<boolean>(true);
  const [selectedCurrency, setSelectedCurrency] = useState<Currency>(
    geolocatedCurrency ?? defaultCurrency
  );

  const annualDiscountAmount = getAnnualDiscountPercentage(planStacks);

  // Load initial currency to overwrite the default state
  const setInitialCurrency = (currency: Currency) => {
    // Avoid resetting the state on load if account currency is *already* the default currency
    if (currency.name !== defaultCurrency.name) {
      setSelectedCurrency((prevState: Currency) => {
        return { ...prevState, ...currency };
      });
    }
  };

  useEffect(() => {
    if (!isLoadingCurrency && displayCurrency) {
      setInitialCurrency(displayCurrency);

      if (currencyType === currencyTypeName.GEOLOCATED) {
        trackEvent(PLAN_CHANGE_EVENT, {
          absolute_url: window.location.href,
          ...geoCurrencyLoadedEventData,
          option_selected: displayCurrency.name,
        });
      }
    }
    // Adding setSelectedCurrency here creates a loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencyTypeName, displayCurrency, isLoadingCurrency]);

  const getTooltipPosition = (index: number): TooltipPosition => {
    const max = { small: 1, medium: 3, large: 5 }[size];

    if (size === "small" || index === 0 || index % max === 0) {
      return "northwest";
    }

    if (
      (planStacks.length <= max && index === planStacks.length - 1) ||
      (index + 1) % max === 0
    ) {
      return "northeast";
    }

    return "north";
  };

  return (
    <div css={Styles.root}>
      {!isLoadingCurrency &&
        selectedCurrency?.name !== defaultCurrency.name && (
          <CurrencyChargeWarningBar
            margin="20px 0 10px 0"
            selectedCurrencyCode={selectedCurrency.name}
          />
        )}
      <PlansConfigBar>
        <div css={Styles.currencyDropdown}>
          {!isLoadingCurrency && (
            <CurrencyDropdown
              onSelectCurrency={setSelectedCurrency}
              selectedCurrency={selectedCurrency}
              useQuery={useQuery as any}
              css={css`
                width: 100% !important;
              `}
            />
          )}
        </div>
        <div css={hiddenOnMobile}>
          <IntervalToggle
            annualDiscountPercent={annualDiscountAmount}
            isAnnual={isAnnual}
            onChange={setIsAnnual}
            css={hiddenOnMobile}
          />
        </div>
        <div css={[Styles.intervalDropdown, visibleOnlyOnMobile]}>
          <IntervalDropdown
            annualDiscountPercent={annualDiscountAmount}
            isAnnual={isAnnual}
            onChange={setIsAnnual}
          />
        </div>
      </PlansConfigBar>
      {isLoadingPlanStacks ? (
        <SkeletonLoader>
          <SkeletonBlock height={415} width="100%" />
        </SkeletonLoader>
      ) : (
        <div css={Styles.cards}>
          {plans.map((plan, index) => {
            const planStack = planStacks.find(
              (planStack) => planStack.planType === plan.type
            );

            if (!planStack) {
              return null;
            }

            return (
              <div key={`${plan.type}_${index}`}>
                <PlanCard
                  currency={selectedCurrency}
                  isAnnual={isAnnual}
                  description={plan.description}
                  features={plan.features}
                  planStack={planStack}
                  hidePrice={plan.hidePrice}
                  hideTasks={plan.hideTasks}
                  primaryCTA={plan.primaryCTA}
                  secondaryCTA={plan.secondaryCTA}
                  previousPlanType={index > 0 ? plans[index - 1].type : null}
                  priceDetails={plan.priceDetails}
                  tooltipPosition={getTooltipPosition(index)}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
